<template>
  <div class="quote-request">
    <h2 class="mb-1">Submit a Quote Request</h2>

    <div v-if="submitSuccess">
      <p class="alert alert-success">Thank you. Your request was sent successfully.</p>
    </div>

    <div v-if="! distributors.length > 0 && ! submitSuccess">
      <p class="mb-2">First enter your ZIP code to find your local distributor.</p>

      <form method="post" class="d-flex">
        <div class="me-2 mb-0">
          <label for="zip" class="visually-hidden">ZIP Code <span>*</span></label>
          <input 
            type="text" 
            v-model="userData.zip" 
            id="zip" 
            class="form-control" 
            placeholder="ZIP Code*" 
            maxlength="5" 
            required
            @keydown.enter.prevent="findDistributors"
          >
        </div>

        <button type="button" class="btn btn-primary" v-bind:disabled="isSubmitting" @click="findDistributors">
          <div 
            class="spinner-border spinner-border-sm" 
            role="status" 
            style="margin-right: 4px; margin-left: -5px;" 
            v-if="isSubmitting"
          ></div> 
          Submit
        </button>
      </form>
    </div>

    <form method="post" v-if="distributors.length > 0 && ! submitSuccess">
      <div class="row g-3 mb-3">
        <div class="col-12">
          <div class="form-group">
            <label for="distributor" class="visually-hidden">Select a Distributor <span>*</span></label>
            <select v-model="userData.distributor" id="distributor" class="form-select" required>
              <option value="">Select a Distributor*</option>
              <option
                v-for="distributor in distributors" 
                v-bind:value="distributor.id"
              >
                {{ distributor.name }}: {{ distributor.city }}, {{ distributor.state }}
              </option>
            </select>
          </div>
        </div>

        <div class="col-lg-6">
          <label for="name" class="visually-hidden">Name <span>*</span></label>
          <input type="text" v-model="userData.name" id="name" class="form-control" placeholder="Name*" required>
        </div>

        <div class="col-lg-6">
          <label for="email" class="visually-hidden">Email <span>*</span></label>
          <input type="email" v-model="userData.email" id="email" class="form-control" placeholder="Email*" required>
        </div>

        <div class="col-12">
          <label for="comments" class="visually-hidden">Comments/Questions</label>
          <textarea v-model="userData.comments" id="comments" class="form-control" placeholder="Comments/Questions"></textarea>
        </div>
      </div> <!-- .row -->

      <button type="button" class="btn btn-primary" v-bind:disabled="isSubmitting" @click="submitForm">
        <div 
          class="spinner-border spinner-border-sm" 
          role="status" 
          style="margin-right: 4px; margin-left: -5px;" 
          v-if="isSubmitting"
        ></div> 
        Submit
      </button>
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert'
import Vue from 'vue'

export default {
  props: {
    products: [Array, Object],
    geometry: Object,
  },
  data() {
    return {
      isSubmitting: false,
      submitSuccess: false,
      distributors: [],
      userData: {
        zip: null,
        name: null,
        email: null,
        comments: null,
        distributor: '',
      }
    }
  },
  methods: {
    /**
     * Find distributor by ZIP code.
     */
    findDistributors() {
      if (
        ! this.userData.zip || 
        this.userData.zip.length != 5
      ) {
        swal('Validation Error', 'Please enter a 5-digit ZIP code.', 'error')
        return
      }

      this.isSubmitting = true

      axios.get('/distributors/search', {
        params: {
          type: 'json',
          search: this.userData.zip
        }
      })
      .then(response => {
        if (response.data.result == 0) {
          swal('Error', 'An unexpected error occurred.', 'error')
          return
        }

        if (response.data.distributors) {
          this.distributors = response.data.distributors
        } else {
          swal('No Results', 'No distributors were found from your search.', 'info')
        }
      })
      .catch(error => {
        swal('Error', 'An unexpected error occurred.', 'error')
      })
      .then(response => {
        this.isSubmitting = false
      })
    },
    /**
     * Submit the quote request form.
     */
    submitForm() {
      if (
        ! this.userData.distributor ||
        ! this.userData.name ||
        ! this.userData.email
      ) {
        swal('Validation Error', 'Please enter all required fields.', 'error')
        return
      }

      this.isSubmitting = true

      let payload = this.userData
      payload.products = this.products
      payload.geometry = this.geometry

      axios.post('/quote-request', payload)
        .then(response => {
          this.distributors = []
          this.submitSuccess = true
          this.$emit('success')
        })
        .catch(error => {
          const msg = document.createElement('div')
          msg.innerHTML = this.getErrorMessageFromResponse(error)

          swal({
            content: msg,
            icon: 'error',
          })
        })
        .then(response => {
          this.isSubmitting = false
        });
    },
    /**
     * Collect and format an error message from an ajax response.
     * @see https://github.com/axios/axios#handling-errors
     */
    getErrorMessageFromResponse(error) {
      if (
        error.response && 
        error.response.data && 
        error.response.data.message
      ) {
        return '<p class="alert alert-danger text-start">' + error.response.data.message + '</p>'
      }

      if (
        error.response && 
        error.response.data &&
        error.response.data.errors
      ) {
        let msg = '<ul class="alert alert-danger text-start">'
        for (const index in error.response.data.errors) {
          msg += '<li>' + error.response.data.errors[index] + '</li>'
        }
        msg += '</ul>'

        return msg
      }

      if (
        error.response && 
        error.response.data
      ) {
        let msg = '<ul class="alert alert-danger text-start">'
        for (const index in error.response.data) {
          msg += '<li>' + error.response.data[index] + '</li>'
        }
        msg += '</ul>'

        return msg
      }

      return '<p class="alert alert-danger text-start">An unknown error occurred.</p>'
    },
  }
}
</script>
